import { ThemeColors } from '@evrekadev/evreka-ui-components'
import { AIActions, ActionType } from '../types'
import { useSelector } from 'react-redux'
import { selectClientId } from 'services/slices/auth'
import { useAIContext } from '../context/AIProvider'
import { useGetRouteAssignmentAnalysisQuery } from 'services/ai'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo } from 'react'
import { DataError } from '../components/DataError'
import Chart from 'react-apexcharts'
import { InputSkeleton } from 'components/Skeletons'
import { Flex } from 'components'
import { useTranslation } from 'utils/useTranslation'

const MapsLocation02Icon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={24}
    height={24}
    color={'#000000'}
    fill={'none'}
    {...props}
  >
    <path
      d="M22 10.5V9.71749C22 7.77787 22 6.80807 21.4142 6.2055C20.8284 5.60294 19.8856 5.60294 18 5.60294H15.9214C15.004 5.60294 14.9964 5.60116 14.1715 5.18834L10.8399 3.52114C9.44884 2.82504 8.75332 2.47699 8.01238 2.50118C7.27143 2.52537 6.59877 2.91808 5.25345 3.70351L4.02558 4.42037C3.03739 4.99729 2.54329 5.28576 2.27164 5.76564C2 6.24553 2 6.82993 2 7.99873V16.2157C2 17.7514 2 18.5193 2.34226 18.9467C2.57001 19.231 2.88916 19.4222 3.242 19.4856C3.77226 19.5808 4.42148 19.2018 5.71987 18.4437C6.60156 17.929 7.45011 17.3944 8.50487 17.5394C9.38869 17.6608 10.21 18.2185 11 18.6138"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M8 2.5L8 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path
      d="M15 5.5V9.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.5 12C19.9353 12 22 14.0165 22 16.4629C22 18.9482 19.9017 20.6924 17.9635 21.8783C17.8223 21.9581 17.6625 22 17.5 22C17.3375 22 17.1777 21.9581 17.0365 21.8783C15.1019 20.6808 13 18.9568 13 16.4629C13 14.0165 15.0647 12 17.5 12Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M17.5 16.5H17.509"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const RouteAssignment: React.FC = () => {
  const clientId = useSelector(selectClientId)
  const { setData } = useAIContext()
  const { t } = useTranslation('pages.ai.legends')

  const { data, isLoading, isError } = useGetRouteAssignmentAnalysisQuery(
    clientId ? { clientId } : skipToken,
  )

  useEffect(() => data && setData(JSON.stringify(data)), [data, setData])

  const parsedData = useMemo(() => {
    const regions: string[] = []
    const done: number[] = []
    const undone: number[] = []

    for (const item of data ?? []) {
      const { done_task_count, region_name, task_count } = item
      regions.push(region_name)
      done.push(done_task_count)
      undone.push(task_count - done_task_count)
    }
    return { regions, done, undone }
  }, [data])

  if (isLoading) {
    return (
      <Flex directionColumn="zero" gap={1}>
        <InputSkeleton />
        <InputSkeleton />
        <InputSkeleton />
        <InputSkeleton />
        <InputSkeleton />
      </Flex>
    )
  }

  if (isError) {
    return <DataError />
  }
  return (
    <div
      style={{
        overflowY: 'scroll',
        height: '100%',
      }}
    >
      <Chart
        options={{
          xaxis: {
            categories: parsedData?.regions,
            position: 'top',
          },
          legend: {
            position: 'top',
          },
          chart: {
            type: 'bar',
            stacked: true,
            zoom: {
              enabled: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: true,
            },
          },
          tooltip: {
            shared: true,
            intersect: false,
          },
        }}
        series={[
          {
            name: t('done'),
            data: parsedData?.done,
            color: ThemeColors.pink500,
          },
          {
            name: t('undone'),
            data: parsedData?.undone,
            color: ThemeColors.grey400,
          },
        ]}
        type="bar"
        width={'100%'}
        height={800}
      />
    </div>
  )
}

export const assignmentAnalysis: ActionType = {
  action: AIActions.ASSIGNMENT_ANALYSIS,
  title: 'assignment_analysis.title',
  description: 'assignment_analysis.description',
  icon: <MapsLocation02Icon height={40} width={40} color={ThemeColors.grey700} />,
  dataComponent: <RouteAssignment />,
}
